import { useState, useEffect } from "react";
import moment from "moment";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";

import "../Schedule.scss";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Alert from "components/molecules/Alert/Alert";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import { handleRenderAddressOption } from "helpers/schedulehelper";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import PickupDeliveryDate from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryDate";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import InputField from "components/atoms/InputField/InputField";
import { getAirlineDetail, getAirports, getFlightDetail } from "api";
import { ICountries } from "pages/shipper/RequestForm/components/Schedule/Types";
import { getDateTimeInEnglish } from "helpers/dateTimeHelper";
import { IAddressOption, IAirlineAddress, IAirportAddress } from "../ScheduleTypes";

interface FlightProps {
  index: number;
  countries: ICountries[];
  remove: (index: number) => void;
}

// TODO: REFACTOR THIS COMPONENT, SO MESSY
// USE USEMUTATION AND USEQUERY INSTEAD OF API CALLS
// STATE MANAGEMENT IS ALL OVER THE PLACE
function FlightComponent(props: FlightProps) {
  const { index, countries, remove } = props;

  const [holidays, setHolidays] = useState({});
  const [showEditManually, setShowEditManually] = useState<boolean>(false);
  const [searchAirport, setSearchAirport] = useState<string>("");
  const [airportAddress, setAirportAddress] = useState<IAirportAddress[]>([]);
  const [searchAirline, setSearchAirline] = useState<string>("");
  const [airlineAddress, setAirlineAddress] = useState<IAirlineAddress[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [routes, setRoutes] = useState<{ value: string; label: string }[]>([]);
  const [hasErrorAutoFlight, setHasErrorAutoFlight] = useState(false);

  const intl = useIntl();
  const methods = useFormContext();
  const { setValue, getValues, watch } = methods;

  const airline = watch(`schedule.flights.${index}.airline_name`);
  const departureDate =
    watch(`schedule.flights.${index}.departure_date`) &&
    moment(watch(`schedule.flights.${index}.departure_date`)).format("DD MMM YYYY");
  const flightNumber = watch(`schedule.flights.${index}.flight_number`);

  useEffect(() => {
    const airlineCode = getValues(`schedule.flights.${index}.airline_code`);
    const arrivalAirportCode = getValues(`schedule.flights.${index}.arrival_airport_code`);
    const departureAirportCode = getValues(`schedule.flights.${index}.departure_airport_code`);
    const isManual = getValues(`schedule.flights.${index}.is_manual`);
    setShowEditManually(isManual);
    if (airlineCode) {
      setSearchAirline(airlineCode);
    }
    if (arrivalAirportCode) {
      setSearchAirport(arrivalAirportCode);
    }
    if (departureAirportCode) {
      setSearchAirport(departureAirportCode);
    }
  }, []);

  useEffect(() => {
    // debounce 300ms for airport search
    if (searchAirport) {
      const timeoutId = setTimeout(() => {
        getAirportAddressOptions();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchAirport]);

  useEffect(() => {
    // debounce 300ms for airline search
    if (searchAirline) {
      const timeoutId = setTimeout(() => {
        getAirlineAddressOptions();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchAirline]);

  useEffect(() => {
    if (airline && flightNumber && departureDate) {
      fetchAutoFlight();
    }
  }, [airline, flightNumber, departureDate]);

  const validateFlightNumber = (value: string) => {
    const isValid = Number.isInteger(Number(value));
    return isValid || intl.formatMessage({ id: "validation.validNumber", defaultMessage: "Please enter a number" });
  };

  const fetchAutoFlight = async (inputRoute?: string) => {
    if (showEditManually) return;
    setIsLoading(true);
    setHasErrorAutoFlight(false);
    setRoutes([]);
    try {
      const { data } = await getFlightDetail({
        params: {
          airline,
          flight_number: flightNumber,
          departure_date: getDateTimeInEnglish(watch(`schedule.flights.${index}.departure_date`)),
          ...(inputRoute && { route: inputRoute }),
        },
      });
      setRoutes([]);

      // If no route is available in the response, set the error
      if (data.error && data.has_more_route === false) {
        setHasErrorAutoFlight(true);
      }

      // If there is more than 1 route available
      else if (typeof data.routes === "object" && Object.keys(data.routes).length > 0) {
        const routesData: any = Object.entries(data.routes).map(([key, value]) => ({ value: key, label: value }));
        setRoutes(routesData);
      }

      // If arrival airport, means we have found the flight
      else if (data.arrival_airport_text) {
        setValue(`schedule.flights.${index}.route`, data.routes);
        setFormDataFromFlightEndpoint(data);
      }

      // If route is available in the response, trigger the API call again with route
      else if (data.routes) {
        await fetchAutoFlight(data.routes);
      }
    } catch (error) {
      errorToast(error);
    }
    setIsLoading(false);
  };

  const setFormDataFromFlightEndpoint = (data: any) => {
    const {
      flight_summary,
      on_time_text,
      arrival_date,
      arrival_time,
      arrival_airport_text,
      departure_time,
      departure_airport_text,
    } = data;
    setValue(`schedule.flights.${index}.airline_summary`, flight_summary);
    setValue(`schedule.flights.${index}.on_time_text`, on_time_text);
    setValue(`schedule.flights.${index}.departure_time`, departure_time);
    setValue(`schedule.flights.${index}.departure_airport_name`, departure_airport_text);
    setValue(`schedule.flights.${index}.departure_airport_code`, getAirportCode(departure_airport_text));
    setValue(`schedule.flights.${index}.arrival_date`, moment(arrival_date).format("YYYY-MM-DD"));
    setValue(`schedule.flights.${index}.arrival_time`, arrival_time);
    setValue(`schedule.flights.${index}.arrival_airport_name`, arrival_airport_text);
    setValue(`schedule.flights.${index}.arrival_airport_code`, getAirportCode(arrival_airport_text));
  };

  const getAirportAddressOptions = () => {
    getAirports(searchAirport)
      .then((res) => {
        const result = res.data.results;
        setAirportAddress(result);
        const airportDetail = result.filter((ap: IAirportAddress) => ap.code === searchAirport);
        if (airportDetail?.length > 0 && !showEditManually) {
          const { name, code } = airportDetail[0];
          setValue(`schedule.flights.${index}.arrival_airport_code`, `(${code}) ${name}`);
          setValue(`schedule.flights.${index}.departure_airport_code`, `(${code}) ${name}`);
        }
      })
      .catch((error) => errorToast(error));
  };

  const getAirlineAddressOptions = () => {
    getAirlineDetail(searchAirline, { params: { page_size: "max" } })
      .then((res) => {
        const result = res.data.results;
        setAirlineAddress(result);
        const airlineDetail = result.filter((al: IAirlineAddress) => al.api_code === searchAirline);
        if (airlineDetail?.length > 0) {
          const { name, api_code } = airlineDetail[0];
          setValue(`schedule.flights.${index}.airline_name`, `${name}(${api_code})`);
        }
      })
      .catch((error) => errorToast(error));
  };

  const handleSelectDepartureAirport = (item: IAirportAddress[], index: number) => {
    setValue(`schedule.flights.${index}.departure_airport_code`, item?.[0]?.code || "");
    setAirportAddress([]);
  };

  const handleSelectArrivalAirport = (item: IAirportAddress[], index: number) => {
    setValue(`schedule.flights.${index}.arrival_airport_code`, item?.[0]?.code || "");
    setAirportAddress([]);
  };

  const handleSelectAirline = (item: IAddressOption[], index: number) => {
    if (item?.length > 0) {
      setValue(`schedule.flights.${index}.airline_code`, `${item[0].code}`);
    }
  };

  const handleShowEditManually = () => {
    setShowEditManually(true);
    setValue(`schedule.flights.${index}.is_manual`, "true");
  };

  const handleHideEditManually = () => {
    setShowEditManually(false);
    setValue(`schedule.flights.${index}.is_manual`, "false");
  };

  function getAirportCode(name: string) {
    return name?.split("(")[1].split(")")[0];
  }

  return (
    <>
      <div>
        <p className="font-b4 mt-4">
          <FormattedMessage id="quote.provider.schedule.flight" defaultMessage="Flight" />
          <span className="ml-1">{index + 1}</span>
        </p>
        <div className="flex-row mt-2 mb-3">
          <div className="w-192 mt-1">
            <SearchSelect
              preselect={false}
              name={`schedule.flights.${index}.airline_name`}
              label={intl.formatMessage({
                id: "quote.provider.schedule.flights",
                defaultMessage: "Airline",
              })}
              shouldUnregister
              placeholder={intl.formatMessage({
                id: "report.search",
                defaultMessage: "Search",
              })}
              onInputChange={(val: string) => {
                setSearchAirline(val);
              }}
              options={airlineAddress.map((option) => ({
                id: option.id,
                label: option.name,
                value: `${option.name} (${option.api_code})`,
                code: option.code || option.api_code,
              }))}
              onSelect={(item: IAddressOption[]) => {
                handleSelectAirline(item, index);
              }}
              filterBy={["label", "code"]}
              handleRenderOption={handleRenderAddressOption}
              required
            />
          </div>
          <div className="w-90">
            <InputField
              label={intl.formatMessage({
                id: "quote.provider.schedule.flightNumber",
                defaultMessage: "Flight number",
              })}
              validations={{
                shouldUnregister: true,
                validate: validateFlightNumber,
              }}
              type="textinput"
              field_id={`schedule.flights.${index}.flight_number`}
              required
            />
          </div>
          {!showEditManually && (
            <div className="w-192 align-date">
              <label className="required">
                <FormattedMessage id="quote.provider.schedule.departureDate" defaultMessage="Departure date" />
              </label>
              <PickupDeliveryDate
                datePickerProps={{
                  name: `schedule.flights.${index}.departure_date`,
                  required: true,
                }}
                countries={countries}
                holidays={holidays}
                setHolidays={setHolidays}
              />
            </div>
          )}
        </div>

        {!showEditManually && routes.length > 1 && (
          <SearchSelect
            classes="mb-3"
            options={routes}
            name={`schedule.flights.${index}.route`}
            label={intl.formatMessage({
              id: "tracking.components.editScheduleDialog.route",
              defaultMessage: "Route",
            })}
            placeholder={intl.formatMessage({
              id: "general.select",
              defaultMessage: "Select",
            })}
            onSelect={async (item: any[]) => {
              const route = item[0];
              await fetchAutoFlight(route.value);
            }}
            required
          />
        )}
      </div>
      {isLoading && airline && flightNumber && departureDate && (
        <div className="flex-row mb-24">
          <Alert variant="info" className="flight-alert-width">
            <FormattedMessage id="quote.provider.schedule.retrieveFlightData" defaultMessage="Retrieving flight data..." />
          </Alert>
        </div>
      )}
      {getValues(`schedule.flights.${index}.airline_summary`) &&
      getValues(`schedule.flights.${index}.on_time_text`) &&
      !hasErrorAutoFlight &&
      !isLoading ? (
        <div className="mb-24">
          <Alert variant="success" className="flight-alert-width">
            <Row>
              <Col className="text-dark">{getValues(`schedule.flights.${index}.airline_summary`)}</Col>
              <Col className="d-flex justify-content-end align-items-end">
                {getValues(`schedule.flights.${index}.on_time_text`)}
              </Col>
            </Row>
          </Alert>
        </div>
      ) : !isLoading && hasErrorAutoFlight && !showEditManually ? (
        <div className="flex-row mb-24">
          <Alert variant="warning" className="flight-alert-width">
            <FormattedMessage
              id="quote.provider.schedule.flightNotAvailable"
              defaultMessage="We could not find this flight. Please check the details and try again or add this flight manually"
            />
          </Alert>
        </div>
      ) : null}
      <div className="flex-row">
        <div className="w-192 align-date d-flex align-items-center">
          <span className="mt-1">
            <Button
              variant="outline-primary"
              className="px-3"
              size="sm"
              label={intl.formatMessage({ id: "quote.provider.schedule.editManually", defaultMessage: "Edit manually" })}
              onClick={handleShowEditManually}
            />
          </span>
          <span className="ml-3 cursor-pointer" onClick={() => remove(index)}>
            <CircleCross width={14} height={14} stroke="black" style={{ transform: "scale(1.35)", marginLeft: 4 }} />
          </span>
        </div>
      </div>
      {(showEditManually || getValues(`schedule.flights.${index}.is_manual`) === "true") && (
        <>
          <div>
            <div className="flex-row mt-3 mb-3">
              <div className="w-192 align-date">
                <label className="required">
                  <FormattedMessage id="quote.provider.schedule.departureDate" defaultMessage="Departure date" />
                </label>
                <PickupDeliveryDate
                  datePickerProps={{
                    name: `schedule.flights.${index}.departure_date`,
                    required: true,
                    registerOptions: {
                      deps: `schedule.flights.${index}.arrival_date`,
                    },
                  }}
                  countries={countries}
                  holidays={holidays}
                  setHolidays={setHolidays}
                />
              </div>
              <div className="mt-2">
                <label />
                <TimeInput
                  name={`schedule.flights.${index}.departure_time`}
                  outputFormat="HH:mm"
                  required
                  shouldUnregister
                  registerOptions={{
                    deps: `schedule.flights.${index}.arrival_date`,
                  }}
                />
              </div>
              <div className="w-192 mt-1">
                <SearchSelect
                  shouldUnregister
                  preselect={false}
                  name={`schedule.flights.${index}.departure_airport_name`}
                  defaultValue={getValues(`schedule.flights.${index}.departure_airport_name`)}
                  placeholder={intl.formatMessage({
                    id: "report.search",
                    defaultMessage: "Search",
                  })}
                  onInputChange={(val: string) => {
                    setSearchAirport(val);
                  }}
                  options={airportAddress.map((option) => ({
                    id: option.id,
                    label: option.name,
                    value: option.name,
                    city_name: option.city_name,
                    country_name: option.country_name,
                    country_code: option.country_code,
                    code: option.code || option.un_locode,
                  }))}
                  label={intl.formatMessage({
                    id: "quote.provider.schedule.departureAirport",
                    defaultMessage: "Departure airport",
                  })}
                  onSelect={(item: IAirportAddress[]) => {
                    handleSelectDepartureAirport(item, index);
                  }}
                  filterBy={["label", "city_name", "country_name", "code"]}
                  handleRenderOption={handleRenderAddressOption}
                  required
                />
              </div>
            </div>
            <div className="flex-row">
              <div className="w-192 align-date">
                <label className="required">
                  <FormattedMessage id="quote.provider.schedule.arrivalDate" defaultMessage="Arrival date" />
                </label>
                <PickupDeliveryDate
                  datePickerProps={{
                    name: `schedule.flights.${index}.arrival_date`,
                    shouldUnregister: true,
                    required: true,
                  }}
                  countries={countries}
                  holidays={holidays}
                  setHolidays={setHolidays}
                />
              </div>
              <div className="mt-2">
                <label />
                <TimeInput
                  name={`schedule.flights.${index}.arrival_time`}
                  outputFormat="HH:mm"
                  required
                  shouldUnregister
                  registerOptions={{
                    deps: `schedule.flights.${index}.arrival_date`,
                  }}
                />
              </div>
              <div className="w-192 mt-1">
                <SearchSelect
                  shouldUnregister
                  name={`schedule.flights.${index}.arrival_airport_name`}
                  defaultValue={getValues(`schedule.flights.${index}.arrival_aiport_name`)}
                  placeholder={intl.formatMessage({
                    id: "report.search",
                    defaultMessage: "Search",
                  })}
                  onInputChange={(val: string) => {
                    setSearchAirport(val);
                  }}
                  label={intl.formatMessage({
                    id: "quote.provider.arrivalAirport",
                    defaultMessage: "Arrival airport",
                  })}
                  options={airportAddress.map((option) => ({
                    id: option.id,
                    label: option.name,
                    value: option.name,
                    city_name: option.city_name,
                    country_name: option.country_name,
                    country_code: option.country_code,
                    code: option.code || option.un_locode,
                  }))}
                  onSelect={(item: IAirportAddress[]) => {
                    handleSelectArrivalAirport(item, index);
                  }}
                  filterBy={["label", "city_name", "country_name", "code"]}
                  handleRenderOption={handleRenderAddressOption}
                  required
                />
              </div>
            </div>
          </div>
          <div className="flex-row mt-3 mb-24">
            <div className="w-192 align-date">
              <Button
                variant="outline-primary"
                className="px-3"
                size="sm"
                label={intl.formatMessage({
                  id: "quote.provider.schedule.automaticLookup",
                  defaultMessage: "Automatic lookup",
                })}
                onClick={handleHideEditManually}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default FlightComponent;
